import { jsPDF } from 'jspdf';
import { toast } from 'sonner';
import { I18N } from './i18n';
import { APP_INFO, getLogo, fill } from './license';
import { clean, loadImg, mapSnapshot, download, projectStem } from './exporters';
import { MAINT, objPos } from './maint';


// one-page repair certificate for the client (before/after photos, location map, signature)
export async function repairPdf(project, o, entry, lang, kind = 'fitting') {
  const t = I18N[lang] || I18N.de;
  const rp = entry.repair;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210, M = 15, CW = W - 2 * M;
  const fmt = (d) => new Date(d).toLocaleString(t.locale, { dateStyle: 'medium', timeStyle: 'short' });
  doc.setFillColor(14, 30, 48); doc.rect(0, 0, W, 26, 'F');
  const logo = getLogo();
  if (logo) {
    const li = await loadImg(logo, false);
    if (li) { const r = li.width / li.height, w = Math.min(36, r * 14); doc.addImage(logo, 'PNG', W - M - w, 6, w, w / r); }
  }
  doc.setTextColor(255); doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.text(clean(t.rrTitle), M, 13);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.text(clean(`${project.title} · ${fmt(Date.now())}`).slice(0, 95), M, 20);
  let y = 36;
  doc.setTextColor(20, 20, 20);
  const pos = objPos(o, kind, project.fittings || []);
  const typeLabel = kind === 'fitting' ? o.cat?.name || t['fit_' + o.type] : kind === 'pipe' ? o.cat?.name || `DN ${o.dn || ''}` : t.pitCount;
  const rows = [
    [t.mwoPart, `${o.name || '-'}${o.serial ? ` [${o.serial}]` : ''}`],
    [t.mwoType, `${t['objKind_' + kind]} – ${typeLabel || ''}`],
    [t.rrCoords, pos ? `${(+pos[0]).toFixed(6)}, ${(+pos[1]).toFixed(6)}` : '-'],
    [t.mtRepairPrev, `${t['mt_' + rp.prev] || '-'}${rp.prevTags?.length ? ` – ${rp.prevTags.join(', ')}` : ''}`],
    [t.mtRepairDate, fmt(rp.date)],
    [t.mtRepairBy, rp.by],
    [t.mtRepairNote, rp.note || '-'],
    [t.mwoStatus, t.mt_ok],
  ];
  const mapW = 62, mapH = 50;
  rows.forEach(([k, v]) => {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(clean(k), M, y);
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(clean(String(v)), CW - mapW - 48);
    doc.text(lines, M + 40, y); y += Math.max(6, lines.length * 4.6 + 1.4);
  });
  if (pos) {
    const SW = 620, SH = 500;
    const snap = await mapSnapshot({ lat: pos[0], lon: pos[1], zoom: 19 }, [], { markers: [{ pt: pos, n: '✓', color: MAINT.ok }], noOverlay: true, noCross: true, t }, SW, SH);
    if (snap) { doc.addImage(snap, 'JPEG', W - M - mapW, 34, mapW, mapH); doc.setFontSize(7.5); doc.setTextColor(110); doc.text(clean(t.rrMap), W - M - mapW, 34 + mapH + 3.5); doc.setTextColor(20, 20, 20); }
  }
  y = Math.max(y, 34 + mapH + 8) + 2;
  const ph = rp.photos || {};
  const pics = ['before', 'after'].filter((w) => ph[w]);
  if (pics.length) {
    const pw = (CW - 6) / 2, maxH = 95;
    pics.forEach((w, k) => {
      const x = M + k * (pw + 6), h = Math.min(maxH, (pw * ph[w].h) / ph[w].w);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text(clean(t[w === 'before' ? 'mtBefore' : 'mtAfter']), x, y);
      doc.addImage(ph[w].data, 'JPEG', x, y + 2, pw, h);
    });
    y += maxH + 8;
  } else {
    doc.setFont('helvetica', 'italic'); doc.setFontSize(9); doc.text(clean(t.rrNoPhotos), M, y); y += 10;
  }
  const sy = Math.max(y + 8, 252);
  doc.setDrawColor(120); doc.setLineWidth(0.3);
  doc.line(M, sy, M + 75, sy); doc.line(W - M - 75, sy, W - M, sy);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(90);
  doc.text(clean(fill(t.rrSignTech, { by: rp.by })), M, sy + 4.5);
  doc.text(clean(t.rrSignClient), W - M - 75, sy + 4.5);
  doc.text(clean(t.rrPlaceDate), M, sy + 9); doc.text(clean(t.rrPlaceDate), W - M - 75, sy + 9);
  doc.setFontSize(7.5); doc.text(clean(`${APP_INFO.name} v${APP_INFO.version} | ${APP_INFO.publisher} (${APP_INFO.web})`), M, 290);
  return doc.output('blob');
}

export async function shareRepairPdf(project, o, entry, lang, kind) {
  const t = I18N[lang] || I18N.de;
  const blob = await repairPdf(project, o, entry, lang, kind);
  const name = `${projectStem(project)}_${t.rrFile}_${clean(o.name || 'obj').replace(/[^\w-]+/g, '_')}_${new Date(entry.repair.date).toISOString().slice(0, 10)}.pdf`;
  const file = new File([blob], name, { type: 'application/pdf' });
  if (navigator.canShare?.({ files: [file] })) {
    try { await navigator.share({ files: [file], title: name, text: `${t.rrTitle}: ${o.name || ''} – ${project.title}` }); } catch (e) { if (e?.name !== 'AbortError') throw e; }
    return;
  }
  download(blob, name);
  toast.success(t.rrSaved);
}
